import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, getDoc, query, where, orderBy, limit,
  serverTimestamp, onSnapshot, increment, writeBatch,
  getCountFromServer
} from 'firebase/firestore';
import { db } from './config';

// ── ARTICLES ──────────────────────────────────────────────────
export const addArticle = async (data) => {
  return await addDoc(collection(db, 'articles'), {
    ...data,
    viewCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateArticle = async (id, data) => {
  await updateDoc(doc(db, 'articles', id), { ...data, updatedAt: serverTimestamp() });
};

export const deleteArticle = async (id) => {
  await deleteDoc(doc(db, 'articles', id));
};

export const getPublishedArticles = async (limitCount = 20) => {
  const q = query(
    collection(db, 'articles'),
    where('status', '==', 'published'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAllArticles = async () => {
  const q = query(collection(db, 'articles'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getArticleById = async (id) => {
  const snap = await getDoc(doc(db, 'articles', id));
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
};

export const getArticlesByCategory = async (category) => {
  const q = query(
    collection(db, 'articles'),
    where('status', '==', 'published'),
    where('category', '==', category),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const incrementArticleView = async (id) => {
  await updateDoc(doc(db, 'articles', id), { viewCount: increment(1) });
};

export const getTopArticles = async (limitCount = 10) => {
  const q = query(
    collection(db, 'articles'),
    where('status', '==', 'published'),
    orderBy('viewCount', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const subscribeToPublishedArticles = (callback, language = 'all') => {
  // Always fetch ALL published articles ordered by date (no language filter in Firestore).
  // Client-side filtering handles language — this avoids needing composite indexes.
  const q = query(
    collection(db, 'articles'),
    where('status', '==', 'published'),
    orderBy('createdAt', 'desc'),
    limit(200)
  );
  return onSnapshot(q, (snap) => {
    let articles = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Client-side language filter
    if (language && language !== 'all') {
      articles = articles.filter(a => {
        // New format: languages is an array ['english', 'telugu']
        if (Array.isArray(a.languages)) return a.languages.includes(language);
        // Old format: language is a comma-separated string 'english,telugu'
        if (a.language) return a.language.split(',').map(l => l.trim().toLowerCase()).includes(language);
        // Fallback: treat as english only
        return language === 'english';
      });
    }

    callback(articles);
  });
};

// ── E-PAPERS ──────────────────────────────────────────────────
export const addEPaper = async (data) => {
  return await addDoc(collection(db, 'epapers'), {
    ...data,
    viewCount: 0,
    uploadedAt: serverTimestamp()
  });
};

export const getAllEPapers = async () => {
  const q = query(collection(db, 'epapers'), orderBy('date', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getEPaperByDate = async (date) => {
  const q = query(collection(db, 'epapers'), where('date', '==', date));
  const snap = await getDocs(q);
  if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
  return null;
};

export const deleteEPaper = async (id) => {
  await deleteDoc(doc(db, 'epapers', id));
};

export const incrementEPaperView = async (id) => {
  await updateDoc(doc(db, 'epapers', id), { viewCount: increment(1) });
};

// ── ADVERTISEMENTS ────────────────────────────────────────────
export const addAd = async (data) => {
  return await addDoc(collection(db, 'advertisements'), {
    ...data,
    createdAt: serverTimestamp()
  });
};

export const updateAd = async (id, data) => {
  await updateDoc(doc(db, 'advertisements', id), data);
};

export const deleteAd = async (id) => {
  await deleteDoc(doc(db, 'advertisements', id));
};

export const getAllAds = async () => {
  const snap = await getDocs(collection(db, 'advertisements'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getActiveAdsByType = async (adType) => {
  const q = query(
    collection(db, 'advertisements'),
    where('adType', '==', adType),
    where('isActive', '==', true)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const subscribeToActiveAds = (adType, callback) => {
  const q = query(
    collection(db, 'advertisements'),
    where('adType', '==', adType),
    where('isActive', '==', true)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

// ── CATEGORIES ────────────────────────────────────────────────
export const addCategory = async (name) => {
  const slug = name.toLowerCase().replace(/\s+/g, '-');
  return await addDoc(collection(db, 'categories'), {
    name, slug, createdAt: serverTimestamp()
  });
};

export const getCategories = async () => {
  const q = query(collection(db, 'categories'), orderBy('name'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteCategory = async (id) => {
  await deleteDoc(doc(db, 'categories', id));
};

// ── ANALYTICS / PAGE VISITS ───────────────────────────────────
export const trackPageVisit = async (data) => {
  await addDoc(collection(db, 'pageVisits'), {
    ...data,
    visitedAt: serverTimestamp()
  });
};

export const getVisitStats = async () => {
  const now = new Date();
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - 7);
  const startOfMonth = new Date(now); startOfMonth.setDate(now.getDate() - 30);
  const startOfYear = new Date(now); startOfYear.setFullYear(now.getFullYear(), 0, 1);

  const allSnap = await getDocs(collection(db, 'pageVisits'));
  const all = allSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  const toMs = (v) => v?.toDate ? v.toDate().getTime() : new Date(v).getTime();

  const filter = (from, type) => all.filter(v => {
    const t = toMs(v.visitedAt);
    const afterFrom = !from || t >= from.getTime();
    const matchType = !type || v.pageType === type;
    return afterFrom && matchType;
  });

  return {
    today: { total: filter(startOfDay).length, site: filter(startOfDay, null).filter(v => v.pageType !== 'epaper').length, epaper: filter(startOfDay, 'epaper').length },
    week: { total: filter(startOfWeek).length, site: filter(startOfWeek).filter(v => v.pageType !== 'epaper').length, epaper: filter(startOfWeek, 'epaper').length },
    month: { total: filter(startOfMonth).length, site: filter(startOfMonth).filter(v => v.pageType !== 'epaper').length, epaper: filter(startOfMonth, 'epaper').length },
    year: { total: filter(startOfYear).length, site: filter(startOfYear).filter(v => v.pageType !== 'epaper').length, epaper: filter(startOfYear, 'epaper').length },
    allTime: { total: all.length, site: all.filter(v => v.pageType !== 'epaper').length, epaper: all.filter(v => v.pageType === 'epaper').length }
  };
};

export const getDailyVisits = async (days = 30) => {
  const snap = await getDocs(collection(db, 'pageVisits'));
  const all = snap.docs.map(d => d.data());
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
    const next = new Date(d); next.setDate(d.getDate() + 1);
    const label = d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
    const dayVisits = all.filter(v => {
      const t = v.visitedAt?.toDate ? v.visitedAt.toDate() : new Date(v.visitedAt);
      return t >= d && t < next;
    });
    result.push({ date: label, site: dayVisits.filter(v => v.pageType !== 'epaper').length, epaper: dayVisits.filter(v => v.pageType === 'epaper').length });
  }
  return result;
};

export const getMonthlyVisits = async (months = 12) => {
  const snap = await getDocs(collection(db, 'pageVisits'));
  const all = snap.docs.map(d => d.data());
  const result = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(); d.setMonth(d.getMonth() - i, 1); d.setHours(0, 0, 0, 0);
    const next = new Date(d); next.setMonth(d.getMonth() + 1);
    const label = d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    const monthVisits = all.filter(v => {
      const t = v.visitedAt?.toDate ? v.visitedAt.toDate() : new Date(v.visitedAt);
      return t >= d && t < next;
    });
    result.push({ month: label, site: monthVisits.filter(v => v.pageType !== 'epaper').length, epaper: monthVisits.filter(v => v.pageType === 'epaper').length });
  }
  return result;
};

export const getActiveVisitors = async () => {
  const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
  const snap = await getDocs(collection(db, 'pageVisits'));
  const sessions = new Set();
  snap.docs.forEach(d => {
    const data = d.data();
    const t = data.visitedAt?.toDate ? data.visitedAt.toDate() : new Date(data.visitedAt);
    if (t >= fiveMinAgo) sessions.add(data.sessionId);
  });
  return sessions.size;
};

export const getVisitsByPageType = async () => {
  const snap = await getDocs(collection(db, 'pageVisits'));
  const counts = {};
  snap.docs.forEach(d => {
    const { pageType } = d.data();
    counts[pageType] = (counts[pageType] || 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
};